<form
  [formGroup]="filterFormGroup()"
  (ngSubmit)="triggerSearch()"
  role="search"
  [attr.aria-label]="'Filtri di ricerca'"
>
  <!-- Main search bar and action buttons -->
  <div class="ml-3 flex items-center pb-0 pt-4">
    @if (areSelectInput()) {
      <mat-form-field
        class="mat-search-input bg-white col-span-2 w-105.25 m-h-[56px]! h-14! outline-added"
        appearance="outline"
        [attr.aria-label]="inputSearchName()"
      >
        <mat-select
          [placeholder]="
            inputSearchPlaceholder() ?? 'GENERAL.search' | translate
          "
          [formControlName]="inputSearchName()"
          [attr.id]="inputSearchName()"
          [attr.aria-label]="inputSearchName()"
        >
          @for (item of selectOptions(); track item) {
            <mat-option [value]="item">{{ item?.name }}</mat-option>
          }
        </mat-select>
      </mat-form-field>
    } @else {
      <mat-form-field
        class="mat-search-input bg-white col-span-2 w-105.25 m-h-[56px]! h-14! outline-added"
        appearance="outline"
      >
        <input
          matInput
          [placeholder]="
            inputSearchPlaceholder() ?? 'GENERAL.search' | translate
          "
          [formControlName]="inputSearchName()"
          [attr.aria-label]="inputSearchPlaceholder() | translate"
          [attr.aria-controls]="tableReference()"
          [attr.id]="inputSearchName()"
          type="text"
          autocomplete="on"
        />
        <mat-icon matSuffix>search</mat-icon>
      </mat-form-field>
    }
    <button
      type="button"
      mat-icon-button
      class="col-span-1 ml-4"
      (click)="toggleFiltri()"
      [attr.aria-label]="'GENERAL.show_advanced_search' | translate"
      [matTooltip]="'GENERAL.show_advanced_search' | translate"
    >
      <mat-icon
        class="material-icons-outlined"
        [matBadge]="activeFilters.length"
        matBadgeColor="warn"
        aria-hidden="false"
        [matBadgeHidden]="activeFilters.length === 0"
        >filter_alt</mat-icon
      >
    </button>

    <button
      mat-icon-button
      type="button"
      class="col-span-1 ml-4"
      [disabled]="!hasActiveFilters"
      [attr.aria-disabled]="!hasActiveFilters"
      [attr.aria-label]="'GENERAL.reset' | translate"
      [matTooltip]="'GENERAL.reset' | translate"
      (click)="resetAllFilters()"
    >
      <mat-icon class="material-icons-outlined" aria-hidden="true"
        >delete</mat-icon
      >
    </button>
  </div>

  <!-- Active filter chips -->
  @if (activeFilters.length > 0) {
    <div class="flex flex-wrap items-center gap-4 px-3 mb-3 mt-3">
      @for (filter of activeFilters; track filter.key) {
        <mat-chip-row class="bg-gray-100" (removed)="removeFilter(filter.key)">
          <strong>{{ filter.label | translate }}:</strong>&nbsp;{{
            transformFilterVelue(filter)
          }}
          <button
            matChipRemove
            [attr.aria-label]="'Remove filter ' + (filter.label | translate)"
          >
            <mat-icon>cancel</mat-icon>
          </button>
        </mat-chip-row>
      }
    </div>
  }

  <!-- Filter sheet backdrop and panel -->
  @if (isFiltriVisible()) {
    <div
      class="filtri-backdrop"
      (click)="closeFiltri()"
      tabindex="0"
      (keypress)="closeFiltri()"
    ></div>
  }

  <div class="filtri-panel pb-5" [class.open]="isFiltriVisible()">
    <div
      class="flex flex-wrap gap-4 p-4 filter-container"
      [formGroup]="filterFormGroup()"
    >
      <div
        class="text-2xl font-normal w-full flex justify-between items-center mb-5"
      >
        <div>{{ 'GENERAL.search' | translate }}</div>
        <button
          mat-icon-button
          (click)="closeFiltri()"
          class="pr-2"
          [attr.aria-label]="'Chiudi menu'"
          [matTooltip]="'Chiudi menu'"
        >
          <mat-icon>close</mat-icon>
        </button>
      </div>

      <!-- Dynamic filter fields -->
      @for (field of filterItems(); track field.controlName) {
        <!-- Render different field components based on type -->
        @if (field.type === 'text') {
          <siat-input-field
            [controlName]="field.controlName"
            [formGroup]="filterFormGroup()"
            [label]="field.label | translate"
            [placeholder]="field.placeholder | translate"
            class="w-full"
          ></siat-input-field>
        }
        @if (field.type === 'select-tipologica') {
          <siat-select-tipologica-field
            [controlName]="field.controlName"
            [formGroup]="filterFormGroup()"
            [label]="field.label | translate"
            [endpoint]="field.endpoint"
            [placeholder]="field.placeholder | translate"
            class="w-full"
          ></siat-select-tipologica-field>
        }
        @if (field.type === 'checkbox') {
          <siat-checkbox-field
            [formGroup]="filterFormGroup()"
            [controlName]="field.controlName"
            [label]="field.label | translate"
            class="w-full"
          ></siat-checkbox-field>
        }
        @if (field.type === 'select') {
          <siat-select-field
            [controlName]="field.controlName"
            [formGroup]="filterFormGroup()"
            [label]="field.label | translate"
            [showLabel]="true"
            [items]="field.options()"
            class="w-full"
            [placeholder]="field.placeholder | translate"
          ></siat-select-field>
        }
        @if (field.type === 'date') {
          <siat-datepicker-field
            [controlName]="field.controlName"
            [formGroup]="filterFormGroup()"
            [label]="field.label | translate"
            class="w-full"
            [placeholder]="field.placeholder | translate"
          ></siat-datepicker-field>
        }
        @if (field.type === 'multi-select') {
          <siat-multiselect-field
            [controlName]="field.controlName"
            [label]="field.label | translate"
            class="w-full"
            [items]="field.options()"
            [formGroup]="filterFormGroup()"
            [placeholder]="field.placeholder | translate"
          ></siat-multiselect-field>
        }
        @if (field.type === 'toggle') {
          <siat-switch-field
            [controlName]="field.controlName"
            class="w-full"
            [formGroup]="filterFormGroup()"
            [label]="field.label | translate"
          ></siat-switch-field>
        }
        @if (field.type === 'radio-button') {
          <siat-radio-field
            [controlName]="field.controlName"
            [formGroup]="filterFormGroup()"
            [label]="field.label | translate"
          ></siat-radio-field>
        }
      }
    </div>

    <!-- Action buttons inside the filter panel -->
    <div class="flex justify-around mt-5">
      <button
        type="submit"
        mat-flat-button
        (keydown)="triggerSearch()"
        (click)="triggerSearch()"
        [attr.aria-label]="'GENERAL.search' | translate"
        [matTooltip]="'GENERAL.search' | translate"
      >
        <mat-icon>search</mat-icon>
        {{ 'GENERAL.search' | translate }}
      </button>
      <button
        mat-stroked-button
        class=""
        type="reset"
        [attr.aria-label]="'GENERAL.clear_filter' | translate"
        [matTooltip]="'GENERAL.clear_filter' | translate"
        (click)="resetAllFilters()"
      >
        <mat-icon>clear_all</mat-icon>
        {{ 'GENERAL.clear_filter' | translate }}
      </button>
    </div>
  </div>
</form>
